import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

interface Node {
  id: string;
  text: string;
  x: number;
  y: number;
  type: 'start' | 'end' | 'process' | 'coordinator' | 'scoring' | 'calibration' | 'error';
  color: string;
  description?: string;
}

interface Connection {
  from: string;
  to: string;
  label?: string;
  curve?: 'left' | 'right';
  color?: string;
  dashed?: boolean;
}

const ScoringWorkflowDiagram: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);

  // Define nodes with positions, types, and descriptions
  const nodes: Node[] = [
    {
      id: "start",
      text: "Document Input",
      x: 500,
      y: 80,
      type: "start",
      color: "#6b7280",
      description: "Talent card documents uploaded to the system"
    },
    {
      id: "doc_processing",
      text: "Document Processing",
      x: 500,
      y: 200,
      type: "process",
      color: "#10b981",
      description: "Extract structured data from talent cards using AI"
    },
    {
      id: "supervisor",
      text: "Supervisor Agent",
      x: 500,
      y: 320,
      type: "coordinator",
      color: "#3b82f6",
      description: "Central orchestrator managing workflow routing and state transitions"
    },
    {
      id: "scoring_coordinator",
      text: "Scoring Coordinator",
      x: 500,
      y: 440,
      type: "coordinator",
      color: "#3b82f6",
      description: "Delegates evaluation to specialized dimension agents"
    },

    // Scoring agents in parallel (arranged in a grid)
    {
      id: "ambiguity_agent",
      text: "Ambiguity",
      x: 200,
      y: 600,
      type: "scoring",
      color: "#10d9a0",
      description: "Evaluates ability to handle unclear requirements and complex situations"
    },
    {
      id: "communication_agent",
      text: "Communication",
      x: 380,
      y: 600,
      type: "scoring",
      color: "#10d9a0",
      description: "Evaluates clarity and effectiveness of communication"
    },
    {
      id: "execution_agent",
      text: "Execution",
      x: 560,
      y: 600,
      type: "scoring",
      color: "#10d9a0",
      description: "Measures quality and effectiveness of work delivery"
    },
    {
      id: "impact_agent",
      text: "Impact",
      x: 200,
      y: 720,
      type: "scoring",
      color: "#10d9a0",
      description: "Assesses organizational and business impact"
    },
    {
      id: "scope_agent",
      text: "Scope & Influence",
      x: 380,
      y: 720,
      type: "scoring",
      color: "#10d9a0",
      description: "Assesses breadth, scale, and sphere of influence"
    },
    {
      id: "process_agent",
      text: "Process Improvement",
      x: 560,
      y: 720,
      type: "scoring",
      color: "#10d9a0",
      description: "Evaluates process improvement and operational excellence"
    },

    {
      id: "calibration",
      text: "Calibration Agent",
      x: 500,
      y: 880,
      type: "calibration",
      color: "#f59e0b",
      description: "Analyzes results, generates insights, and ensures consistency"
    },
    {
      id: "completion",
      text: "Completion Handler",
      x: 500,
      y: 1000,
      type: "process",
      color: "#10b981",
      description: "Finalizes processing and updates statistics"
    },
    {
      id: "end",
      text: "Results & Insights",
      x: 500,
      y: 1120,
      type: "end",
      color: "#6b7280",
      description: "Comprehensive scoring and actionable recommendations"
    },
    {
      id: "error_handler",
      text: "Error Recovery",
      x: 850,
      y: 600,
      type: "error",
      color: "#ef4444",
      description: "Handles failures and manages error recovery"
    }
  ];

  // Define connections with labels
  const connections: Connection[] = [
    { from: "start", to: "doc_processing" },
    { from: "doc_processing", to: "supervisor" },
    { from: "supervisor", to: "scoring_coordinator", label: "route" },

    // Coordinator to all scoring agents
    { from: "scoring_coordinator", to: "ambiguity_agent", curve: "left" },
    { from: "scoring_coordinator", to: "communication_agent", curve: "left" },
    { from: "scoring_coordinator", to: "execution_agent" },
    { from: "scoring_coordinator", to: "impact_agent", curve: "left" },
    { from: "scoring_coordinator", to: "scope_agent", curve: "left" },
    { from: "scoring_coordinator", to: "process_agent" },

    // All scoring agents to calibration
    { from: "ambiguity_agent", to: "calibration", curve: "right" },
    { from: "communication_agent", to: "calibration", curve: "right" },
    { from: "execution_agent", to: "calibration" },
    { from: "impact_agent", to: "calibration", curve: "right" },
    { from: "scope_agent", to: "calibration", curve: "right" },
    { from: "process_agent", to: "calibration" },

    { from: "calibration", to: "completion" },
    { from: "completion", to: "end" },

    // Error handling paths
    { from: "doc_processing", to: "error_handler", curve: "right", color: "#ef4444", dashed: true },
    { from: "scoring_coordinator", to: "error_handler", curve: "right", color: "#ef4444", dashed: true },
    { from: "error_handler", to: "supervisor", curve: "right", color: "#ef4444", dashed: true }
  ];

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Function to create curved or straight path
  const createPath = (source: Node, target: Node, curve?: string): string => {
    const dx = target.x - source.x;
    const dy = target.y - source.y;

    if (curve === "left") {
      const controlX = source.x + dx * 0.3;
      const controlY = source.y + dy * 0.3;
      return `M${source.x},${source.y + 30} Q${controlX},${controlY} ${target.x},${target.y - 30}`;
    } else if (curve === "right") {
      const controlX = source.x + dx * 0.7;
      const controlY = source.y + dy * 0.7;
      return `M${source.x},${source.y + 30} Q${controlX},${controlY} ${target.x},${target.y - 30}`;
    } else {
      return `M${source.x},${source.y + 30} L${target.x},${target.y - 30}`;
    }
  };

  // Calculate chart bounds for fit-to-view functionality
  const getChartBounds = () => {
    const padding = 60;
    const minX = Math.min(...nodes.map(n => n.x - 100)) - padding;
    const maxX = Math.max(...nodes.map(n => n.x + 100)) + padding;
    const minY = Math.min(...nodes.map(n => n.y - 30)) - padding;
    const maxY = Math.max(...nodes.map(n => n.y + 30)) + padding;

    return {
      width: maxX - minX,
      height: maxY - minY,
      x: minX,
      y: minY
    };
  };

  // Initialize D3 chart
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous render

    // Create zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Create main group for all chart elements
    const g = svg.append("g");

    // Create arrow marker
    const defs = g.append("defs");

    defs.append("marker")
      .attr("id", "arrowhead")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 8)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#64748b");

    defs.append("marker")
      .attr("id", "arrowhead-error")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 8)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#ef4444");

    // Draw connections
    connections.forEach(conn => {
      const sourceNode = nodes.find(n => n.id === conn.from);
      const targetNode = nodes.find(n => n.id === conn.to);

      if (sourceNode && targetNode) {
        const pathData = createPath(sourceNode, targetNode, conn.curve);

        g.append("path")
          .attr("d", pathData)
          .attr("fill", "none")
          .attr("stroke", conn.color || "#64748b")
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", conn.dashed ? "5,5" : "none")
          .attr("marker-end", conn.color === "#ef4444" ? "url(#arrowhead-error)" : "url(#arrowhead)")
          .attr("opacity", 0.7);
      }
    });

    // Draw nodes
    const nodeGroups = g.selectAll<SVGGElement, Node>(".node")
      .data(nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", d => `translate(${d.x}, ${d.y})`)
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        event.stopPropagation();
        setSelectedNode(d);
      });

    // Add rectangles for nodes
    nodeGroups.append("rect")
      .attr("width", d => {
        const baseWidth = d.type === 'scoring' ? 140 : 180;
        return Math.max(d.text.length * 9, baseWidth);
      })
      .attr("height", 60)
      .attr("x", d => {
        const baseWidth = d.type === 'scoring' ? 140 : 180;
        return -(Math.max(d.text.length * 9, baseWidth)) / 2;
      })
      .attr("y", -30)
      .attr("rx", d => d.type === "start" || d.type === "end" ? 30 : 8)
      .attr("ry", d => d.type === "start" || d.type === "end" ? 30 : 8)
      .attr("fill", d => {
        if (d.type === 'start' || d.type === 'end') return d.color;
        return '#ffffff';
      })
      .attr("stroke", d => d.color)
      .attr("stroke-width", d => d.type === 'coordinator' ? 3 : 2)
      .attr("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.1))");

    // Add text labels
    nodeGroups.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .attr("fill", d => {
        if (d.type === 'start' || d.type === 'end') return '#ffffff';
        return d.color;
      })
      .attr("font-size", d => d.type === 'scoring' ? "13px" : "14px")
      .attr("font-weight", d => d.type === 'coordinator' ? "600" : "500")
      .text(d => d.text);

    // Add annotation for parallel processing
    g.append("text")
      .attr("x", 380)
      .attr("y", 550)
      .attr("text-anchor", "middle")
      .attr("fill", "#6b7280")
      .attr("font-size", "12px")
      .attr("font-style", "italic")
      .text("6 Scoring Agents Execute in Parallel");

    // Store zoom behavior for button controls
    (svg.node() as any).__zoom__ = zoom;

    // Auto-fit on initial render
    setTimeout(() => fitToView(), 100);

  }, [dimensions, nodes, connections]);

  // Zoom control functions
  const zoomIn = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      (svg.node() as any).__zoom__.scaleBy, 1.5
    );
  };

  const zoomOut = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(
      (svg.node() as any).__zoom__.scaleBy, 1 / 1.5
    );
  };

  const resetZoom = () => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(500).call(
      (svg.node() as any).__zoom__.transform,
      d3.zoomIdentity
    );
  };

  const fitToView = () => {
    if (!svgRef.current || dimensions.width === 0) return;

    const bounds = getChartBounds();
    const containerWidth = dimensions.width - 40;
    const containerHeight = dimensions.height - 40;

    const scale = Math.min(
      containerWidth / bounds.width,
      containerHeight / bounds.height
    ) * 0.85;

    const translate = [
      (containerWidth - bounds.width * scale) / 2 - bounds.x * scale + 20,
      (containerHeight - bounds.height * scale) / 2 - bounds.y * scale + 20
    ];

    const svg = d3.select(svgRef.current);
    svg.transition().duration(750).call(
      (svg.node() as any).__zoom__.transform,
      d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
    );
  };

  return (
    <div className="w-full h-[900px] bg-white border border-gray-300 overflow-hidden relative">
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <button
          onClick={zoomIn}
          className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 cursor-pointer shadow-sm"
          title="Zoom in"
        >
          +
        </button>
        <button
          onClick={zoomOut}
          className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 cursor-pointer shadow-sm"
          title="Zoom out"
        >
          −
        </button>
        <button
          onClick={resetZoom}
          className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 cursor-pointer shadow-sm"
          title="Reset zoom"
        >
          Reset
        </button>
        <button
          onClick={fitToView}
          className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 cursor-pointer shadow-sm"
          title="Fit to view"
        >
          Fit
        </button>
      </div>

      {/* Node detail panel */}
      {selectedNode && (
        <div className="absolute top-2 left-2 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-xs">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">{selectedNode.text}</h3>
            <button
              onClick={() => setSelectedNode(null)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              title="Close"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600">{selectedNode.description}</p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: selectedNode.color }}
            />
            <span className="text-xs text-gray-500 capitalize">
              {selectedNode.type === 'scoring' ? 'Scoring Agent' :
               selectedNode.type === 'coordinator' ? 'Orchestrator' :
               selectedNode.type === 'calibration' ? 'Calibration' :
               selectedNode.type === 'error' ? 'Error Handler' :
               selectedNode.type}
            </span>
          </div>
        </div>
      )}

      {/* SVG container */}
      <div ref={containerRef} className="w-full h-full">
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          style={{ backgroundColor: '#fafafa', cursor: 'grab' }}
          onMouseDown={(e) => e.currentTarget.style.cursor = 'grabbing'}
          onMouseUp={(e) => e.currentTarget.style.cursor = 'grab'}
          onMouseLeave={(e) => e.currentTarget.style.cursor = 'grab'}
          onClick={() => setSelectedNode(null)}
        />
      </div>

      {/* Legend */}
      <div className="absolute bottom-2 left-2 z-10 bg-white/95 border border-gray-300 rounded-lg shadow-sm p-3 text-xs">
        <div className="font-semibold text-gray-700 mb-2">Legend</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3b82f6' }} />
            <span className="text-gray-600">Orchestrator</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }} />
            <span className="text-gray-600">Processing</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10d9a0' }} />
            <span className="text-gray-600">Scoring Agent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }} />
            <span className="text-gray-600">Calibration</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }} />
            <span className="text-gray-600">Error Recovery</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringWorkflowDiagram;
